<template>
  <div class="w-full h-full flex flex-col">
    <div
      v-if="showLegacyControls"
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3"
    >
      <div class="flex gap-2">
        <button @click="mode = 'setores'" :class="buttonClass('setores')">
          Setores
        </button>

        <button @click="mode = 'empresas'" :class="buttonClass('empresas')">
          Empresas
        </button>
      </div>

      <select
        v-model="selected"
        class="bg-zinc-800 text-sm rounded px-3 py-2 border border-zinc-700"
      >
        <option v-for="item in availableItems" :key="item" :value="item">
          {{ item }}
        </option>
      </select>
    </div>

    <BaseChart
      type="line"
      :series="series"
      :categories="displayCategories"
      :extraOptions="extraOptions"
    />
  </div>
</template>

<script>
import BaseChart from "./BaseChart.vue";

export default {
  name: "LinePerformanceChart",

  components: {
    BaseChart,
  },

  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    seriesData: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: String,
      default: "binary_lines",
    },
  },

  data() {
    return {
      mode: "setores",
      selected: null,

      months: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun"],

      // fallback antigo
      dataset: {
        setores: {
          Financeiro: {
            Jan: { yes: 70, no: 30 },
            Fev: { yes: 60, no: 40 },
            Mar: { yes: 75, no: 25 },
            Abr: { yes: 80, no: 20 },
            Mai: { yes: 85, no: 15 },
            Jun: { yes: 90, no: 10 },
          },
          Operacional: {
            Jan: { yes: 50, no: 50 },
            Fev: { yes: 55, no: 45 },
            Mar: { yes: 60, no: 40 },
            Abr: { yes: 65, no: 35 },
            Mai: { yes: 70, no: 30 },
            Jun: { yes: 72, no: 28 },
          },
        },

        empresas: {
          "Empresa A": {
            Jan: { yes: 65, no: 35 },
            Fev: { yes: 70, no: 30 },
            Mar: { yes: 72, no: 28 },
            Abr: { yes: 75, no: 25 },
            Mai: { yes: 78, no: 22 },
            Jun: { yes: 80, no: 20 },
          },
          "Empresa B": {
            Jan: { yes: 65, no: 35 },
            Fev: { yes: 70, no: 30 },
            Mar: { yes: 72, no: 28 },
            Abr: { yes: 75, no: 25 },
            Mai: { yes: 78, no: 22 },
            Jun: { yes: 80, no: 20 },
          },
        },
      },
    };
  },

  computed: {
    hasExternalData() {
      return Array.isArray(this.seriesData) && this.seriesData.length > 0;
    },

    showLegacyControls() {
      return !this.hasExternalData;
    },

    isScale() {
      return this.variant === "scale_mean_line";
    },

    availableItems() {
      return Object.keys(this.dataset[this.mode]);
    },

    currentData() {
      return this.dataset[this.mode][this.selected] || {};
    },

    fallbackSeries() {
      return [
        {
          name: "Sim",
          data: this.months.map((m) => this.currentData[m]?.yes || 0),
        },
        {
          name: "Não",
          data: this.months.map((m) => this.currentData[m]?.no || 0),
        },
      ];
    },

    series() {
      if (this.hasExternalData) {
        return this.seriesData.map((item) => ({
          name: item.label,
          data: item.data || [],
        }));
      }

      return this.fallbackSeries;
    },

    displayCategories() {
      const source = this.hasExternalData ? this.categories : this.months;

      return source.map((value) => {
        const str = String(value);

        if (/^\d{4}-\d{2}$/.test(str)) {
          const [, month] = str.split("-");
          const map = {
            "01": "Jan",
            "02": "Fev",
            "03": "Mar",
            "04": "Abr",
            "05": "Mai",
            "06": "Jun",
            "07": "Jul",
            "08": "Ago",
            "09": "Set",
            10: "Out",
            11: "Nov",
            12: "Dez",
          };

          return map[month] || str;
        }

        return str;
      });
    },

    seriesColors() {
      if (!this.isScale) {
        return ["#22c55e", "#ef4444"];
      }

      return this.series.map((serie) => {
        const values = (serie.data || []).filter(
          (value) => value !== null && value !== undefined
        );

        if (!values.length) return "#f59e0b";

        const avg =
          values.reduce((acc, value) => acc + Number(value || 0), 0) /
          values.length;

        if (avg <= 2.32) return "#ef4444";
        if (avg <= 3.65) return "#f59e0b";
        return "#22c55e";
      });
    },

    extraOptions() {
      return {
        colors: this.seriesColors,

        stroke: {
          width: 3,
          curve: "smooth",
        },

        markers: {
          size: 4,
          strokeWidth: 2,
          hover: {
            size: 6,
          },
        },

        xaxis: {
          labels: {
            style: {
              colors: "#94a3b8",
              fontSize: "12px",
            },
          },
        },

        yaxis: {
          min: 0,
          max: this.isScale ? 5 : 100,
          labels: {
            formatter: (val) =>
              this.isScale ? Number(val).toFixed(0) : `${val}%`,
            style: {
              colors: "#94a3b8",
            },
          },
        },

        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val) =>
              this.isScale ? Number(val).toFixed(2) : `${val}%`,
          },
        },

        legend: {
          position: "top",
          labels: {
            colors: "#d1d5db",
          },
        },

        grid: {
          borderColor: "rgba(255,255,255,0.08)",
        },
      };
    },
  },

  watch: {
    mode() {
      if (!this.hasExternalData) {
        this.selected = this.availableItems[0];
      }
    },
  },

  mounted() {
    if (!this.hasExternalData) {
      this.selected = this.availableItems[0];
    }
  },

  methods: {
    buttonClass(type) {
      return [
        "px-3 py-1 rounded text-sm transition",
        this.mode === type
          ? "bg-green-500 text-white"
          : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700",
      ];
    },
  },
};
</script>
